/**
 * Client-side Merchant Configuration
 *
 * Defines merchant Pokemon, their item pools, and time-based rotation for the shop view.
 * Adapted from server-side merchant config for client use.
 */

import { ITEMS } from "game/data/items";
import { ITEM_NAME } from "game/data/items/item-names";

// Merchant definitions for client-side use
export interface ClientMerchantDefinition {
  id: string;
  pokemon_name: string;
  display_name: string;
  color: string; // Theme color for UI
  description: string;
  sell_item_pool: string[];
  merchant_type: "seller" | "buyer" | "mixed" | "trader";
  price_modifier_range: [number, number];
}

export const CLIENT_MERCHANTS: ClientMerchantDefinition[] = [
  {
    id: "kecleon",
    pokemon_name: "kecleon",
    display_name: "Kenko",
    color: "#8B4513", // Brown
    description: "The Bargain Hunter",
    sell_item_pool: [
      ITEM_NAME.KINGS_ROCK,
      ITEM_NAME.DRAGON_SCALE,
      ITEM_NAME.RAZOR_FANG,
      ITEM_NAME.RAZOR_CLAW,
      ITEM_NAME.REAPER_CLOTH,
      ITEM_NAME.WHIPPED_DREAM,
      ITEM_NAME.SACHET,
      ITEM_NAME.BASIL,
      ITEM_NAME.DEEP_SEA_SCALE,
      ITEM_NAME.DEEP_SEA_TOOTH,
    ],
    merchant_type: "mixed",
    price_modifier_range: [1.5, 1.6],
  },
  {
    id: "delibird",
    pokemon_name: "delibird",
    display_name: "Delibirus",
    color: "#FF6B6B", // Red
    description: "The Delivery Boy",
    sell_item_pool: [
      ITEM_NAME.PRISM_SCALE,
      ITEM_NAME.DRAGON_SCALE,
      ITEM_NAME.FANCY_APPLE,
      ITEM_NAME.SWEET_APPLE,
      ITEM_NAME.SYRUPY_APPLE,
      ITEM_NAME.TART_APPLE,
      ITEM_NAME.PEAT_BLOCK,
      ITEM_NAME.GALARICA_CUFF,
      ITEM_NAME.GALARICA_WREATH

    ],
    merchant_type: "seller",
    price_modifier_range: [1.5, 1.6],
  },

  {
    id: "tinkaton",
    pokemon_name: "tinkaton",
    display_name: "Barbara",
    color: "#4A90E2", // Blue
    description: "The Stone Cutter",
    sell_item_pool: [
      ITEM_NAME.THUNDER_STONE,
      ITEM_NAME.LEAF_STONE,
      ITEM_NAME.WATER_STONE,
      ITEM_NAME.FIRE_STONE,
      ITEM_NAME.ICE_STONE,
      ITEM_NAME.MOON_STONE,
      ITEM_NAME.SUN_STONE,
      ITEM_NAME.DUSK_STONE,
      ITEM_NAME.SHINY_STONE,
      ITEM_NAME.DAWN_STONE,
      ITEM_NAME.OVAL_STONE,
      ITEM_NAME.BLACK_AUGURITE,
      ITEM_NAME.METAL_ALLOY,
      ITEM_NAME.METAL_COAT,
      ITEM_NAME.AUSPICIOUS_ARMOR,
      ITEM_NAME.MALICIOUS_ARMOR,

    ],
    merchant_type: "buyer",
    price_modifier_range: [1.5, 1.6],
  },
  {
    id: "ludicolo",
    pokemon_name: "ludicolo",
    display_name: "Carlos",
    color: "#228B22", // Forest Green
    description: "The Party Master",
    sell_item_pool: [
      ITEM_NAME.PROTECTOR,
      ITEM_NAME.BASIL,
      ITEM_NAME.BERRY_SWEET,
      ITEM_NAME.CLOVER_SWEET,
      ITEM_NAME.FLOWER_SWEET,
      ITEM_NAME.LOVE_SWEET,
      ITEM_NAME.RIBBON_SWEET,
      ITEM_NAME.STAR_SWEET,
      ITEM_NAME.STRAWBERRY_SWEET,
    ],
    merchant_type: "seller",
    price_modifier_range: [1.5, 1.6],
  },

  {
    id: "porygon",
    pokemon_name: "porygon",
    display_name: "Tuk-Tuk",
    color: "#00CED1", // Dark Turquoise
    description: "Technical Support",
    sell_item_pool: [
      ITEM_NAME.UPGRADE,
      ITEM_NAME.DUBIOUS_DISC,
      ITEM_NAME.ELECTIRIZER,
      ITEM_NAME.MAGMARIZER,
      ITEM_NAME.METAL_COAT,
    ],
    merchant_type: "trader",
    price_modifier_range: [1.5, 1.6],
  },
];

const hourMap = {
  0: "kecleon",
  1: "kecleon",
  2: "kecleon",
  3: null,
  4: null,
  5: "delibird",
  6: "delibird",
  7: "delibird",
  8: null,
  9: null,
  10: "tinkaton",
  11: "tinkaton",
  12: "tinkaton",
  13: null,
  14: null,
  15: "ludicolo",
  16: "ludicolo",
  17: "ludicolo",
  18: null,
  19: null,
  20: "porygon",
  21: "porygon",
  22: "porygon",
  23: null,
}

/**
 * Get current merchant based on UTC time (rotates every 6 hours)
 */
export function getCurrentMerchant(): ClientMerchantDefinition | null {
  const now = new Date();
  const utcHour = now.getUTCHours();
  const merchant = hourMap[utcHour as keyof typeof hourMap];


  if (!merchant) return null;

  return CLIENT_MERCHANTS.find(m => m.id === merchant) || null;
}

/**
 * Get next merchant rotation time
 */
export function getNextMerchantRotation(): Date {
  const now = new Date();
  const currentHour = now.getUTCHours();

  // Calculate next 6-hour boundary
  const nextSlotHour = Math.ceil((currentHour + 1) / 6) * 6;
  const nextRotation = new Date(now);
  nextRotation.setUTCHours(nextSlotHour % 24, 0, 0, 0);

  // If it's the next day, add a day
  if (nextSlotHour >= 24) {
    nextRotation.setUTCDate(nextRotation.getUTCDate() + 1);
  }

  return nextRotation;
}

/**
 * Get time until next merchant rotation
 */
export function getTimeUntilNextRotation(): number {
  const nextRotation = getNextMerchantRotation();
  return nextRotation.getTime() - Date.now();
}

/**
 * Format time remaining until next rotation
 */
export function formatTimeUntilNextRotation(): string {
  const msUntil = getTimeUntilNextRotation();
  const hours = Math.floor(msUntil / (1000 * 60 * 60));
  const minutes = Math.floor((msUntil % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

/**
 * Generate random items for current merchant
 */
export function generateMerchantItems(merchant: ClientMerchantDefinition): Array<{
  item_name: string;
  price: number;
  quantity: number;
}> {
  const selectedItems = [...merchant.sell_item_pool]

  return selectedItems.map(itemName => {
    const itemData = ITEMS.find(item => item.item_name === itemName);
    const basePrice = itemData?.shop_price || 1000;

    // Apply random price modifier within merchant's range
    const [minMod, maxMod] = merchant.price_modifier_range;
    const modifier = minMod + Math.random() * (maxMod - minMod);
    const finalPrice = Math.round(basePrice * modifier);

    return {
      item_name: itemName,
      price: finalPrice,
      quantity: Math.floor(Math.random() * 5) + 1, // 1-5 stock
    };
  });
}

/**
 * Get merchant description based on type
 */
export function getMerchantTypeDescription(type: string): string {
  switch (type) {
    case "seller":
      return "Selling items at special prices!";
    case "buyer":
      return "Looking to buy your valuables!";
    case "mixed":
      return "Both buying and selling today!";
    case "trader":
      return "Offering special trades!";
    default:
      return "Here to do business!";
  }
}